import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    message: '',
    to_email: 'nuyuag@gmail.com'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formData,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (response.status !== 200) {
        throw new Error(`Failed to send email: ${response.text}`);
      }

      setSuccess(true);
      setFormData({
        from_name: '',
        from_email: '',
        message: '',
        to_email: 'nuyuag@gmail.com'
      });
    } catch (err) {
      setError('Failed to send message. Please try again later.');
      console.error('Email sending error:', {
        timestamp: new Date().toISOString(),
        error: err,
        formData: {
          from_name: formData.from_name,
          from_email: formData.from_email,
          message: formData.message.substring(0, 100)
        },
        emailjsConfig: {
          serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
          templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, color: 'common.white', fontWeight: 600 }}>
          Send us a Message
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                fullWidth
                name="from_name"
                label="Name"
                value={formData.from_name}
                onChange={handleInputChange}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'common.white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
              <TextField
                fullWidth
                name="from_email"
                label="Your Email"
                type="email"
                value={formData.from_email}
                onChange={handleInputChange}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'common.white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Box>
            <TextField
              fullWidth
              name="message"
              label="Message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'common.white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Message sent successfully!
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'primary.light',
                },
              }}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </form>
      </Paper>
    </motion.div>
  );
};

export default ContactForm;
